import { createSlice } from '@reduxjs/toolkit';

import { PlanboardState } from './types';
import { DEMO_MACHINE_ID, LEGACY_DEMO_MACHINE_ID } from '@components/tours/config/planboard/constants';
import {
  setSelectedMachineGroupIdAction,
  setPlanboardSearchValueAction,
  setIsPlanboardAvailableAction,
  onRescheduledProductionOperationsAction,
  onClosedProductionOperationAction,
  onFailedToCloseProductionOperationAction,
  onFailedToRescheduleProductionOperationsAction,
  clearRescheduleErrorAction,
  clearCloseErrorAction,
  setPendingOperationAction,
  removePendingOperationAction,
  clearExternalMoveDetectedAction,
  clearBackendRescheduleDetectedAction,
  setDemoOperationsAction,
  clearDemoOperationsAction,
  setPendingResetOperationAction,
  clearMachineRefetchAction,
  setColumnReschedulingAction,
  clearPendingResetOperationAction,
  addMachineToRefetchQueueAction,
  setReschedulingFromPositionAction,
  clearReschedulingByCorrelationIdAction,
} from './actions';
import { fetchMachineOperations, fetchNoMachineColumnCount } from './thunks';

const initialState: PlanboardState = {
  machineProductionOperations: {
    [-1]: {
      items: [],
      loading: false,
      total: 0,
    },
  },
  selectedMachineGroupId: null,
  isPlanboardAvailable: false,
  lastCloseError: null,
  lastClosedSnapshot: null,
  pendingOperationIds: [],
  pendingOperations: {},
  externalMoveDetected: null,
  backendRescheduleDetected: null,
  lastRescheduleError: null,
  pendingResetOperations: {},
  machineColumnsNeedingRefetch: [],
  reschedulingMachineIds: [],
  reschedulingFromPosition: {},
};

const planboardSlice = createSlice({
  name: 'planboard',
  initialState,
  reducers: {
    setSelectedMachineGroupId: setSelectedMachineGroupIdAction,
    setPlanboardSearchValue: setPlanboardSearchValueAction,
    setIsPlanboardAvailable: setIsPlanboardAvailableAction,
    onRescheduledProductionOperations: onRescheduledProductionOperationsAction,
    onClosedProductionOperation: onClosedProductionOperationAction,
    onFailedToCloseProductionOperation: onFailedToCloseProductionOperationAction,
    onFailedToRescheduleProductionOperations: onFailedToRescheduleProductionOperationsAction,
    clearRescheduleError: clearRescheduleErrorAction,
    clearCloseError: clearCloseErrorAction,
    setPendingOperation: setPendingOperationAction,
    removePendingOperation: removePendingOperationAction,
    clearExternalMoveDetected: clearExternalMoveDetectedAction,
    clearBackendRescheduleDetected: clearBackendRescheduleDetectedAction,
    setDemoOperations: setDemoOperationsAction,
    clearDemoOperations: clearDemoOperationsAction,
    setPendingResetOperation: setPendingResetOperationAction,
    clearMachineRefetch: clearMachineRefetchAction,
    setColumnRescheduling: setColumnReschedulingAction,
    clearPendingResetOperation: clearPendingResetOperationAction,
    addMachineToRefetchQueue: addMachineToRefetchQueueAction,
    setReschedulingFromPosition: setReschedulingFromPositionAction,
    clearReschedulingByCorrelationId: clearReschedulingByCorrelationIdAction,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMachineOperations.pending, (state, action) => {
        const { machineId, skip } = action.meta.arg;
        const isRefetch = typeof skip !== 'number' || skip === 0;

        // Skip for demo machines - their data is managed separately
        if (machineId === DEMO_MACHINE_ID || machineId === LEGACY_DEMO_MACHINE_ID) {
          return;
        }

        if (!machineId) {
          if (!isRefetch) {
            state.machineProductionOperations[-1].loading = true;
          }
        } else {
          const machineRecord = state.machineProductionOperations[machineId];
          if (machineRecord) {
            if (!isRefetch) {
              state.machineProductionOperations[machineId].loading = true;
            }
          } else {
            state.machineProductionOperations[machineId] = { items: [], loading: true, total: 0 };
          }
        }
      })
      .addCase(fetchMachineOperations.fulfilled, (state, action) => {
        // Skip if payload is null (demo machine fetch was skipped)
        if (!action.payload) {
          return;
        }

        const { response, machineId, reset } = action.payload;

        if (!response) {
          return;
        }

        if (!machineId || machineId === -1) {
          if (reset) {
            state.machineProductionOperations[-1].items = response.searchResult;
          } else {
            state.machineProductionOperations[-1].items = state.machineProductionOperations[-1].items.concat(response.searchResult);
          }
          state.machineProductionOperations[-1].loading = false;

          state.machineProductionOperations[-1].total = response.totalResultCount;
        } else {
          const machineRecord = state.machineProductionOperations[machineId];
          if (machineRecord) {
            if (reset) {
              state.machineProductionOperations[machineId].items = response.searchResult;
            } else {
              state.machineProductionOperations[machineId].items = state.machineProductionOperations[machineId].items.concat(response.searchResult);
            }
            state.machineProductionOperations[machineId].loading = false;
            state.machineProductionOperations[machineId].total = response.totalResultCount;
          } else {
            state.machineProductionOperations[machineId] = {
              items: response.searchResult,
              loading: false,
              total: response.totalResultCount,
            };
          }
        }
      })
      .addCase(fetchMachineOperations.rejected, (state, action) => {
        const machineId = action.meta.arg.machineId;

        if (!machineId) {
          state.machineProductionOperations[-1].loading = false;
        } else {
          const machineRecord = state.machineProductionOperations[machineId];
          if (machineRecord) {
            state.machineProductionOperations[machineId].loading = false;
          }
        }
      })
      .addCase(fetchNoMachineColumnCount.fulfilled, (state, action) => {
        const { linkedCount, totalCount } = action.payload;

        state.machineProductionOperations[-1].paginationMetadata = { linkedCount, totalCount };
      })
      .addCase(fetchNoMachineColumnCount.rejected, (state) => {
        state.machineProductionOperations[-1].paginationMetadata = { linkedCount: 0, totalCount: 0 };
      });
  },
});

// Export actions
export const {
  setSelectedMachineGroupId,
  setPlanboardSearchValue,
  setIsPlanboardAvailable,
  onRescheduledProductionOperations,
  onClosedProductionOperation,
  onFailedToCloseProductionOperation,
  onFailedToRescheduleProductionOperations,
  clearRescheduleError,
  clearCloseError,
  setPendingOperation,
  removePendingOperation,
  clearExternalMoveDetected,
  clearBackendRescheduleDetected,
  setDemoOperations,
  clearDemoOperations,
  setPendingResetOperation,
  clearMachineRefetch,
  setColumnRescheduling,
  clearPendingResetOperation,
  addMachineToRefetchQueue,
  setReschedulingFromPosition,
  clearReschedulingByCorrelationId,
} = planboardSlice.actions;

// Selectors
export const selectSelectedMachienGroupId = (state: { planboard: PlanboardState }) => state.planboard.selectedMachineGroupId;
export const selectPlanboardSearchValue = (state: { planboard: PlanboardState }) => state.planboard.searchValue;

export const selectPlanboardMachineColumn = (columnId: number) => (state: { planboard: PlanboardState }) =>
  state.planboard.machineProductionOperations[columnId];

export const selectLastRescheduleError = (state: { planboard: PlanboardState }) => state.planboard.lastRescheduleError;
export const selectLastCloseError = (state: { planboard: PlanboardState }) => state.planboard.lastCloseError;

// OperationId-first selectors for multi-user safe correlation tracking
export const selectPendingOperations = (state: { planboard: PlanboardState }) => state.planboard.pendingOperations;
export const selectExternalMoveDetected = (state: { planboard: PlanboardState }) => state.planboard.externalMoveDetected;
export const selectBackendRescheduleDetected = (state: { planboard: PlanboardState }) => state.planboard.backendRescheduleDetected;

// Selectors for reset priority feature
export const selectMachineColumnsNeedingRefetch = (state: { planboard: PlanboardState }) =>
  state.planboard.machineColumnsNeedingRefetch;

// Selector for column-wide rescheduling loading state
export const selectReschedulingMachineIds = (state: { planboard: PlanboardState }) =>
  state.planboard.reschedulingMachineIds;

// Selector for position-based rescheduling loading state
export const selectReschedulingFromPosition = (state: { planboard: PlanboardState }) =>
  state.planboard.reschedulingFromPosition;

export default planboardSlice.reducer;
